/**
 * 글 하나의 **표시용 미디어** — 이미지 목록과 영상 메타를 화면이 쓰는 모양으로 고른다.
 *
 * PostDetail 이 「여러 장이면 캐러셀, 한 장이면 그대로」를 판단하는 근거이고,
 * 영상 메타의 빈 칸을 걷어 내는 곳이다. 화면은 이 결과만 그린다.
 */

import type { MomentVideoMeta } from 'src/types/moments/moment-domain';

/** 화면이 받는 글의 미디어 관련 부분만 — 원본이 정규화 전이든 후든 이 모양이면 된다 */
type MediaLike = {
  imagesS3Url?: unknown;
  imageS3Url?: unknown;
  imageUrl?: unknown;
  isVideo?: unknown;
  videosMeta?: unknown;
  videosUrl?: unknown;
};

/**
 * 표시할 이미지 URL 목록.
 *
 * `imagesS3Url` 이 있으면 그것이 정본이고, 없으면 대표 한 장(`imageS3Url` → `imageUrl`)으로
 * 떨어진다. 두 곳 다 비면 빈 배열이다.
 *
 * ⚠️ **대표 한 장을 목록에 덧붙이지 않는다.** `imagesS3Url` 의 첫 장과 같은 그림이라
 * 붙이면 캐러셀 첫 칸이 중복된다.
 *
 * @returns 순서를 보존한 URL 목록. 빈 문자열·비문자열은 걷는다
 */
export function pickPostImages(post: MediaLike | null | undefined): string[] {
  const many = Array.isArray(post?.imagesS3Url)
    ? post.imagesS3Url.map(toUrl).filter(Boolean)
    : [];
  if (many.length > 0) return many;

  const one = toUrl(post?.imageS3Url) || toUrl(post?.imageUrl);
  return one ? [one] : [];
}

/**
 * 그릴 값이 하나라도 있는 영상 메타만 남긴다.
 *
 * 색인에는 `music`·`size` 가 `null` 인 문서가 흔하고, 적을 것이 하나도 없으면 화면에 그릴
 * 상자가 빈다 — 그런 항목은 「영상」이라는 사실만 남기므로 여기서 버린다.
 *
 * ⚠️ `isGenAi` 는 **참일 때만** 적을 거리로 친다. 화면이 「아니오」를 안 그리므로, 거짓만
 * 들어 있는 항목을 남기면 칸이 하나도 없는 빈 상자가 된다.
 *
 * @returns 값이 있는 메타만. 없으면 빈 배열
 */
export function readVideoMetas(post: MediaLike | null | undefined): MomentVideoMeta[] {
  if (!Array.isArray(post?.videosMeta)) return [];

  return post.videosMeta
    .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
    .map((item) => ({
      duration: toNumber(item.duration),
      music: toText(item.music),
      size: toNumber(item.size),
      isGenAi: typeof item.isGenAi === 'boolean' ? item.isGenAi : null,
    }))
    .filter(
      (meta) =>
        meta.duration !== null || meta.music !== null || meta.size !== null || meta.isGenAi === true
    );
}

/**
 * 영상인가.
 *
 * `isVideo` 가 정본이고, 그 칸이 비어 있어도 **영상이 실려 있으면** 영상으로 본다 —
 * 플랫폼마다 `isVideo` 를 채우는 정도가 달라서, 없다고 「사진 글」로 단정하면 틀린다.
 *
 * @returns 영상 여부. 근거가 하나도 없으면 `false`
 */
export function isPostVideo(post: MediaLike | null | undefined): boolean {
  if (post?.isVideo === true) return true;
  if (Array.isArray(post?.videosMeta) && post.videosMeta.length > 0) return true;
  return Array.isArray(post?.videosUrl) && post.videosUrl.some((url) => toUrl(url) !== '');
}

/**
 * 재생 시간을 `1:05` 로 적는다. 한 시간을 넘으면 `1:02:03`.
 *
 * @returns 초가 없으면 빈 문자열
 */
export function formatVideoDuration(seconds: number | null | undefined): string {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return '';

  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return h > 0 ? `${h}:${mm}:${String(s).padStart(2, '0')}` : `${mm}:${String(s).padStart(2, '0')}`;
}

function toUrl(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function toText(value: unknown): string | null {
  const text = typeof value === 'string' ? value.trim() : '';
  return text || null;
}

function toNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
